import { Button } from 'ui';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import CooperativeFormFields, {
  FormFields,
} from 'cooperatives/components/form/CooperativeFormFields';
import { Page } from 'dashboard/components/dashboard/Breadcrumbs';

import { useForm, SubmitHandler, ErrorOption } from 'react-hook-form';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { setFormError } from 'utils/form';
import {
  CooperativeCreated,
  CooperativeCreateMutationVariables,
  COOPERATIVE_CREATE_MUTATION,
} from 'cooperatives/graphql/cooperativeCreateMutation';
import {
  cooperativesTypename,
  CooperativesList,
  COOPERATIVES_QUERY,
} from 'cooperatives/graphql/cooperativesQuery';
import {
  CooperativesSelectList,
  COOPERATIVES_SELECT_QUERY,
  COOPERATIVES_SELECT_QUERY_VARIABLES,
} from 'cooperatives/graphql/cooperativesSelectQuery';
import {
  FINANCIAL_ACCOUNTS_QUERY,
  FINANCIAL_ACCOUNTS_SELECT_QUERY_VARIABLES,
  FinancialAccountList,
} from 'financialAccounts/graphql/financialAccountsQuery';
import useToastContext from 'ui/hooks/useToast';
import { useHistory } from 'react-router';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useLoading } from 'ui/contexts/overlay/Loading';
import { useEffect } from 'react';
import { has } from 'lodash';

const DASHBOARD_COOPERATIVES_ROUTE = '/dashboard/business-entities/cooperatives';

const BREADCRUMB_PAGES: Page[] = [
  { name: 'Cooperativas', route: DASHBOARD_COOPERATIVES_ROUTE, current: false },
  {
    name: 'Nova cooperativa',
    route: null,
    current: true,
  },
];
const TITLE = 'Nova cooperativa';
const CREATE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar criar a cooperativa',
  title: 'Algo deu errado!',
  variant: 'danger',
};

const CREATE_SUCCESS_TOAST: ToastProps = {
  title: 'Sucesso!',
  variant: 'primary',
  text: 'Sucesso ao cadastrar a Cooperativa.',
};

export default function CreateCooperativesPage() {
  const { push } = useHistory();
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormFields>();
  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const [cooperativeCreateMutation, { loading: loadingCreate }] = useMutation<
    CooperativeCreated,
    CooperativeCreateMutationVariables
  >(COOPERATIVE_CREATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'details')) {
        addToast(CREATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof FormFields, error);

          setTimeout(() => clearErrors(), 2500);
        },
        t
      );
    },
    onCompleted() {
      addToast(CREATE_SUCCESS_TOAST);
      push(DASHBOARD_COOPERATIVES_ROUTE);
    },
  });

  const [cooperatives, { data: headquarters, loading, refetch }] =
    useLazyQuery<CooperativesSelectList>(
      COOPERATIVES_SELECT_QUERY,
      COOPERATIVES_SELECT_QUERY_VARIABLES
    );

  const [
    financialAccountsSelect,
    {
      data: dataFinancialAccounts,
      loading: loadingFinancialAccounts,
      refetch: refetchFinancialAccounts,
    },
  ] = useLazyQuery<FinancialAccountList>(
    FINANCIAL_ACCOUNTS_QUERY,
    FINANCIAL_ACCOUNTS_SELECT_QUERY_VARIABLES
  );

  const onSubmit: SubmitHandler<FormFields> = (cooperativeCreateInput) => {
    cooperativeCreateInput.cooperativeAddressPostalCode = Number(
      cooperativeCreateInput.cooperativeAddressPostalCode
    );
    cooperativeCreateInput.cooperativeSubscriptionCostCents = Number(
      cooperativeCreateInput.cooperativeSubscriptionCostCents
    );
    cooperativeCreateMutation({
      variables: {
        cooperativeCreateInput,
      },
      update(cache, { data }) {
        const existingData = cache.readQuery<CooperativesList>({
          query: COOPERATIVES_QUERY,
        });
        cache.writeQuery({
          query: COOPERATIVES_QUERY,
          data: {
            cooperatives: {
              __typename: cooperativesTypename,
              afterCursor: existingData?.cooperatives.afterCursor || null,
              beforeCursor: existingData?.cooperatives.beforeCursor || null,
              entries: [
                data?.cooperativeCreate,
                ...(existingData?.cooperatives.entries || []),
              ],
            },
          },
        });
      },
    });
  };

  const isLoading = loading || loadingCreate || loadingFinancialAccounts;

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  useEffect(() => {
    refetch && refetch();
    cooperatives();
  }, [cooperatives, refetch]);

  useEffect(() => {
    refetchFinancialAccounts && refetchFinancialAccounts();

    financialAccountsSelect();
  }, [financialAccountsSelect, refetchFinancialAccounts]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardHeader={<></>}
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm breadcrumbPages={BREADCRUMB_PAGES} title={TITLE}>
            <Button type="submit" size="sm" disabled={loading}>
              Salvar
            </Button>
          </DashboardMainHeaderForm>
        }
      >
        <CooperativeFormFields
          headquarters={headquarters?.cooperatives.entries}
          financialAccounts={dataFinancialAccounts?.financialAccounts.entries}
          errors={errors}
          register={register}
          control={control}
        />
      </Dashboard>
    </form>
  );
}
